import { ESPLoader, FlashOptions, LoaderOptions, Transport } from "./lib";
import { serial } from "web-serial-polyfill";
import { createTerminal } from "./app/terminal";
import { FirmwareOption, getPrimaryFirmware } from "./app/firmwareCatalog";
if (!navigator.serial && (navigator as any).usb) {
  navigator.serial = serial;
}
declare let CryptoJS: any;
type SelectedFirmware = { data: string; name: string } | null;
function requireElement<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing required element: #${id}`);
  return el as T;
}

const connectButton = requireElement<HTMLButtonElement>("connectButton");
const programButton = requireElement<HTMLButtonElement>("programButton");
const programButtonProgress = requireElement<HTMLElement>("programButtonProgress");
const chooseFileButton = requireElement<HTMLButtonElement>("chooseFileButton");
const targetChipSelect = requireElement<HTMLSelectElement>("targetChipSelect");
const terminalElement = requireElement<HTMLElement>("terminal");
const downloadFirmwareLink = document.getElementById("downloadFirmwareLink") as HTMLAnchorElement | null;
const selectedFirmwareLabel = requireElement<HTMLSpanElement>("selectedFirmwareLabel");
const { term, binding: espLoaderTerminal } = createTerminal(terminalElement);
let selectedFirmware: SelectedFirmware = null;
let currentRecommendedFirmware: FirmwareOption | null = null;
let device: SerialPort | null = null;
let transport: Transport | null = null;
let esploader: ESPLoader | null = null;
let detectedChip: string | null = null;
let isConnected = false;
let warnedMissingDownloadLink = false;
function markConnected(name: string) {
  isConnected = true;
  detectedChip = name;
  connectButton.textContent = `Disconnect: ${name}`;
  connectButton.classList.add("vibe-button--danger");
  programButton.disabled = false;
  programButtonProgress.textContent = "Ready";
}
function clearConnection() {
  isConnected = false;
  detectedChip = null;
  connectButton.textContent = "Connect Device";
  connectButton.classList.remove("vibe-button--danger");
  programButton.disabled = true;
  programButtonProgress.textContent = "Start";
}
function updateManualFirmwareLabel(name: string | null) {
  selectedFirmwareLabel.textContent = name ? `Selected: ${name}` : "No file selected yet.";
}
function resetManualFirmwareSelection() {
  if (!selectedFirmware) return;
  selectedFirmware = null;
  chooseFileButton.textContent = "Choose File";
  updateManualFirmwareLabel(null);
}
function updateDownloadFirmwareLink() {
  if (!downloadFirmwareLink) {
    if (!warnedMissingDownloadLink) {
      console.warn("Skipping firmware link update: #downloadFirmwareLink not found.");
      warnedMissingDownloadLink = true;
    }
    return;
  }
  const target = targetChipSelect.value;
  const option = getPrimaryFirmware(target);
  currentRecommendedFirmware = option;
  if (option) {
    downloadFirmwareLink.href = option.url;
    downloadFirmwareLink.textContent = `Download ${option.label}`;
    downloadFirmwareLink.dataset.firmwareId = option.id;
    downloadFirmwareLink.removeAttribute("aria-disabled");
  } else {
    downloadFirmwareLink.removeAttribute("href");
    downloadFirmwareLink.textContent = "No firmware available";
    downloadFirmwareLink.setAttribute("aria-disabled", "true");
  }
}
async function connectDevice() {
  try {
    if (!device) {
      espLoaderTerminal.writeLine("Requesting device access...");
      device = await navigator.serial.requestPort({});
    }
    transport = new Transport(device, true);
    const loaderOptions: LoaderOptions = {
      transport,
      baudrate: 921600,
      terminal: espLoaderTerminal,
      debugLogging: false,
    };
    esploader = new ESPLoader(loaderOptions);
    const chip = await esploader.main();
    markConnected(chip);
    espLoaderTerminal.writeLine(`Connected to ${chip}`);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(error);
    espLoaderTerminal.writeLine(`Error: ${message}`);
    await disconnectDevice(true);
  }
}
async function disconnectDevice(silent = false) {
  try {
    if (transport) await transport.disconnect();
  } catch (error) {
    console.warn("Transport disconnect failed", error);
  }
  transport = null;
  esploader = null;
  term.reset();
  if (!silent) espLoaderTerminal.writeLine("Disconnected.");
  clearConnection();
}
connectButton.addEventListener("click", async () => {
  if (isConnected) {
    await disconnectDevice();
  } else {
    await connectDevice();
  }
});
targetChipSelect.addEventListener("change", () => {
  updateDownloadFirmwareLink();
  resetManualFirmwareSelection();
});
chooseFileButton.addEventListener("click", () => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".bin";
  input.onchange = (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      if (!e.target?.result) return;
      selectedFirmware = { data: e.target.result as string, name: file.name };
      chooseFileButton.textContent = "Change File";
      updateManualFirmwareLabel(file.name);
      espLoaderTerminal.writeLine(`Selected firmware file: ${file.name}`);
    };
    reader.readAsBinaryString(file);
  };
  input.click();
});
programButton.addEventListener("click", async () => {
  programButton.disabled = true;
  programButtonProgress.textContent = "0%";
  let programmingSucceeded = false;
  try {
    if (!esploader || !isConnected) throw new Error("Device not connected. Please connect first.");
    let firmwareString: string;
    if (selectedFirmware) {
      firmwareString = selectedFirmware.data;
      espLoaderTerminal.writeLine(`Using selected file: ${selectedFirmware.name}`);
    } else {
      const option = currentRecommendedFirmware;
      if (!option) throw new Error("No firmware URL configured for this device.");
      espLoaderTerminal.writeLine(`Loading firmware from: ${option.url}`);
      const response = await fetch(option.url);
      if (!response.ok) throw new Error(`Failed to load firmware file (HTTP ${response.status})`);
      const firmwareData = await response.arrayBuffer();
      const decoder = new TextDecoder("latin1");
      firmwareString = decoder.decode(new Uint8Array(firmwareData));
    }
    let lastProgress = -1;
    const flashOptions: FlashOptions = {
      fileArray: [{ data: firmwareString, address: 0x0000 }],
      flashSize: "keep",
      eraseAll: false,
      compress: true,
      flashMode: "dio",
      flashFreq: "40m",
      reportProgress: (_fileIndex, written, total) => {
        const progress = Math.floor((written / total) * 100);
        if (progress !== lastProgress) {
          programButtonProgress.textContent = `${progress}%`;
          if (progress % 10 === 0) {
            espLoaderTerminal.writeLine(`Programming progress: ${progress}%`);
          }
          if (progress === 100) {
            espLoaderTerminal.writeLine("\x1b[32mVerifying... please wait....\x1b[0m");
          }
          lastProgress = progress;
        }
      },
      calculateMD5Hash: (image) => CryptoJS.MD5(CryptoJS.enc.Latin1.parse(image)),
    };
    espLoaderTerminal.writeLine("\x1b[32mStarting flash process...\x1b[0m");
    await esploader.writeFlash(flashOptions);
    espLoaderTerminal.writeLine("\x1b[32mProgramming completed successfully!\x1b[0m");
    programmingSucceeded = true;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(error);
    espLoaderTerminal.writeLine(`Error: ${message}`);
  } finally {
    programButton.disabled = !isConnected;
    programButtonProgress.textContent = programmingSucceeded ? "Done!" : isConnected ? "Ready" : "Start";
    espLoaderTerminal.writeLine("\x1b[32mPlease reset the device manually.\x1b[0m");
  }
});
clearConnection();
updateManualFirmwareLabel(null);
updateDownloadFirmwareLink();
